/**
 * Data loader for the public bundle offer pages (`/products/<bundle-slug>`).
 *
 * A bundle is still a DISCOUNT, never a SKU: this only resolves the rule and
 * the real products it covers, so the page can price the combination and add
 * the individual SKUs to the cart.
 */
import { supabase } from "@/integrations/supabase/client";
import { fetchProductByHandle, type ShopifyProduct } from "@/lib/shopify";
import { getPackPricingTable } from "@/lib/checkout-pricing";
import {
  DEFAULT_BUNDLE_RULES,
  ruleFromRow,
  ruleRate,
  type BundleRule,
  type BundleRuleRow,
} from "@/lib/bundles";

const COLUMNS =
  "slug,title,tagline,goal,match_handles,quantity_per_item,discount_bps,fixed_price_paise,featured,sort_order,active,starts_at,ends_at";

export interface BundlePageData {
  rule: BundleRule;
  items: ShopifyProduct["node"][];
  qty: number;
  /** Sum of the individual pack prices (rupees). */
  subtotal: number;
  /** What the customer pays for the combination (rupees). */
  total: number;
  savings: number;
  /** Whole-number discount percentage, for copy. */
  percent: number;
}

type Row = BundleRuleRow & {
  active?: boolean | null;
  starts_at?: string | null;
  ends_at?: string | null;
};

function isLive(row: Row, now = Date.now()): boolean {
  if (row.active === false) return false;
  if (row.starts_at && new Date(row.starts_at).getTime() > now) return false;
  if (row.ends_at && new Date(row.ends_at).getTime() <= now) return false;
  return true;
}

/** Resolve a rule by slug from the DB, falling back to the seeded defaults. */
export async function findBundleRule(slug: string): Promise<BundleRule | null> {
  try {
    const { data, error } = await supabase
      .from("bundle_rules")
      .select(COLUMNS)
      .eq("slug", slug)
      .maybeSingle();
    if (!error && data) {
      const row = data as unknown as Row;
      return isLive(row) ? ruleFromRow(row) : null;
    }
  } catch (err) {
    console.warn("[bundle-page] rule lookup failed, using defaults", err);
  }
  return DEFAULT_BUNDLE_RULES.find((r) => r.slug === slug) ?? null;
}

export function priceBundle(
  rule: BundleRule,
  items: ShopifyProduct["node"][],
): Pick<BundlePageData, "qty" | "subtotal" | "total" | "savings" | "percent"> {
  const qty = rule.quantityPerItem || 1;
  // Price members off the canonical single-pack ladder (the same amounts the
  // homepage, PDP and cart show). Shopify's raw first-variant amount is only
  // a fallback — using it made bundles quote a different single price.
  const lineSubtotal = items.reduce((s, p) => {
    const canonical = getPackPricingTable(p.handle)?.[1]?.price;
    const shopify = parseFloat(p.variants.edges[0]?.node.price.amount ?? "0");
    return s + (canonical && canonical > 0 ? canonical : shopify);
  }, 0);
  const subtotal = Math.round(lineSubtotal * qty);
  const total = Math.round(
    rule.fixedPricePaise != null ? rule.fixedPricePaise / 100 : subtotal * (1 - ruleRate(rule)),
  );
  const savings = Math.max(0, subtotal - total);
  const percent = subtotal > 0 ? Math.round((savings / subtotal) * 100) : 0;
  return { qty, subtotal, total, savings, percent };
}

/**
 * Full page payload for a bundle slug, or `null` when the slug isn't a live
 * bundle (or its products can't be resolved) — the caller then 404s.
 */
export async function loadBundlePage(slug: string): Promise<BundlePageData | null> {
  const rule = await findBundleRule(slug);
  if (!rule || rule.matchHandles.length < 2) return null;

  const resolved = await Promise.all(rule.matchHandles.map((h) => fetchProductByHandle(h)));
  const items = resolved.filter((p): p is ShopifyProduct["node"] => !!p);
  if (items.length !== rule.matchHandles.length) return null;

  return { rule, items, ...priceBundle(rule, items) };
}
